// @ts-nocheck
import { TwitchChatClient } from './twitch-chat-client';
import { parseChatMessage, renderChatMessage } from './chat-message-parser';
import { createTwitchChatUrl } from '../../services/twitch-embed';
import { applyTranslations, t } from '../../services/i18n';

/**
 * GlobalChat - Gestionnaire de chat global pour MultiStream
 * Agrège les messages de tous les streams actifs avec les icônes des streamers
 */
export class GlobalChat {
    constructor(chatContainer) {
        this.chatContainer = chatContainer;
        this.activeStreams = new Map(); // Map des streams actifs
        this.chatMessages = []; // Historique des messages
        this.maxMessages = 100; // Limite de messages affichés
        this.isGlobalMode = false;
        this.hiddenStreamers = new Set();
        this.shouldAutoScroll = true;
        this.savedGlobalChatContent = null; // Contenu sauvegardé du chat global
        
        // WebSocket connections pour chaque stream
        this.chatConnections = new Map();
        this.twitchClient = null; // Client Twitch pour les vrais messages
        
        // Caches pour les emotes
        this.bttvEmotesCache = new Map(); // Cache des emotes BetterTTV
        this.ffzEmotesCache = new Map(); // Cache des emotes FrankerFaceZ
        this.sevenTVEmotesCache = new Map(); // Cache des emotes 7TV
        this.globalBTTVEmotes = null; // Emotes BetterTTV globales
        this.globalFFZEmotes = null; // Emotes FrankerFaceZ globales
        this.globalSevenTVEmotes = null; // Emotes 7TV globales
        this.twitchGlobalEmotes = null; // Emotes Twitch globales
        this.twitchChannelEmotesCache = new Map(); // Cache des emotes Twitch par canal
        this.allEmotesCache = new Map(); // Cache combiné des emotes par canal
        this.globalEmotesLoaded = false; // Flag pour savoir si les emotes globales sont chargées
        this.cacheExpiration = 30 * 60 * 1000; // Cache expire après 30 minutes
        this.cacheTimestamps = new Map(); // Timestamps pour l'expiration du cache
        
        // Gestion du nettoyage des messages
        this.messageCleanupInterval = 5 * 60 * 1000; // Nettoyer toutes les 5 minutes
        this.maxMessageAge = 30 * 60 * 1000; // Messages plus anciens que 30 minutes sont supprimés
        this.maxDOMMessages = 150; // Limite d'éléments DOM pour éviter la surcharge
        
        // Bind methods
        this.addMessage = this.addMessage.bind(this);
        this.handleTwitchMessage = this.handleTwitchMessage.bind(this);
        this.cleanOldMessages = this.cleanOldMessages.bind(this);
        
        // Initialiser le client Twitch
        this.initTwitchClient();
        
        // Nettoyer le cache expiré toutes les 10 minutes
        setInterval(() => {
            this.cleanExpiredCache();
        }, 10 * 60 * 1000);
        
        // Nettoyer les anciens messages périodiquement
        setInterval(() => {
            this.cleanOldMessages();
        }, this.messageCleanupInterval);
    }

    /**
     * Initialise le client Twitch pour les vrais messages
     */
    async initTwitchClient() {
        try {
            this.twitchClient = new TwitchChatClient();
            await this.twitchClient.init();
            this.twitchClient.addMessageHandler(this.handleTwitchMessage);
            // Client Twitch initialisé pour le chat global
        
        // Précharger les emotes globales
        await this.preloadGlobalEmotes();
        } catch (error) {
            console.error('❌ Erreur lors de l\'initialisation du client Twitch:', error);
            // Fallback vers les messages simulés si nécessaire
        }
    }


    /**
     * Gestionnaire de messages Twitch
     */
    async handleTwitchMessage(channel, tags, message, self) {
        if (self || !this.isGlobalMode) return;
        
        // Extraire le nom du canal (sans le #)
        const channelName = channel.replace('#', '');
        
        // Trouver le streamer correspondant
        const streamerData = Array.from(this.activeStreams.values())
            .find(s => s.login.toLowerCase() === channelName.toLowerCase());
        
        if (streamerData) {
            // Message reçu dans le canal
            
            // Extraire la couleur de l'utilisateur
            const userColor = tags.color || this.generateUserColor(tags.username);
            
            // Extraire et enrichir les badges avec l'API officielle
            const badges = await this.processBadges(tags.badges, channelName);
            
            this.addMessage(
            streamerData.id,
            tags.username,
            message,
            userColor,
            badges,
            streamerData.profile_image_url,
            channelName,
            tags.emotes,
            tags.gifs,
        );
        }
    }

    /**
     * Active le mode chat global
     */
    async enableGlobalChat(streamers) {
        // S'assurer que le mode est bien désactivé avant de le réactiver
        if (this.isGlobalMode) {
            await this.disableGlobalChat();
        }
        
        this.isGlobalMode = true;
        this.activeStreams.clear();
        
        // Ajouter tous les streamers actifs
        streamers.forEach(streamer => {
            this.activeStreams.set(streamer.id, {
                id: streamer.id,
                login: streamer.login,
                display_name: streamer.display_name,
                profile_image_url: streamer.profile_image_url,
                color: this.generateStreamerColor(streamer.id)
            });
        });
        
        // Précharger les emotes globales
        await this.preloadGlobalEmotes();
        
        // Nettoyer le conteneur avant de restaurer
        this.chatContainer.innerHTML = '';
        this.chatContainer.className = 'global-chat';
        
        // Restaurer le contenu sauvegardé ou rendre une nouvelle interface
        if (this.savedGlobalChatContent) {
            this.chatContainer.innerHTML = this.savedGlobalChatContent;
            this.setupEventListeners();
            this.setupScrollListener();
            // Nettoyer le contenu sauvegardé après restauration
            this.savedGlobalChatContent = null;
        } else {
            this.renderGlobalChatInterface();
        }
        
        // Essayer d'initialiser le client Twitch s'il n'est pas encore prêt
        if (!this.twitchClient || !this.twitchClient.isClientConnected()) {
            // Tentative d'initialisation du client Twitch
            try {
                await this.initTwitchClient();
            } catch (error) {
                console.error('❌ Échec de l\'initialisation du client Twitch:', error);
            }
        }
        
        // Connecter aux canaux Twitch pour les vrais messages
        if (this.twitchClient && this.twitchClient.isClientConnected()) {
            // Client Twitch disponible, connexion aux canaux
            let connectedChannels = 0;
            
            for (const streamer of streamers) {
                try {
                    await this.twitchClient.joinChannel(streamer.login);
                    connectedChannels++;
                    // Connecté au chat du streamer
                } catch (error) {
                    console.error(`❌ Erreur connexion chat ${streamer.display_name}:`, error);
                }
            }
            
            if (connectedChannels === 0) {
                console.warn('⚠️ Aucun canal Twitch connecté, utilisation des messages simulés');
                this.connectToChats();
            } else {
                // Canaux Twitch connectés
            }
        } else {
            console.warn('⚠️ Client Twitch non disponible, utilisation des messages simulés');
            this.connectToChats();
        }
    }

    /**
     * Désactive le mode chat global
     */
    async disableGlobalChat() {
        if (!this.isGlobalMode) {
            return; // Déjà désactivé, ne rien faire
        }
        
        this.isGlobalMode = false;
        
        // Sauvegarder le contenu HTML actuel seulement s'il n'est pas déjà sauvegardé
        if (!this.savedGlobalChatContent && this.chatContainer.innerHTML.trim() !== '') {
            this.savedGlobalChatContent = this.chatContainer.innerHTML;
        }
        
        // Supprimer la classe global-chat du conteneur
        this.chatContainer.className = '';
        
        // Note: On ne déconnecte pas des canaux Twitch et on ne vide pas les messages
        // pour permettre de revenir au chat global avec l'historique intact
        
        // Optionnel: Déconnecter seulement si on veut économiser les ressources
        // this.disconnectFromChats();
    }

    /**
     * Génère une couleur unique pour chaque streamer
     */
    generateStreamerColor(streamerId) {
        const colors = [
            '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
            '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9'
        ];
        const hash = streamerId.split('').reduce((a, b) => {
            a = ((a << 5) - a) + b.charCodeAt(0);
            return a & a;
        }, 0);
        return colors[Math.abs(hash) % colors.length];
    }

    /**
     * Génère une couleur pour un utilisateur
     */
    generateUserColor(username) {
        const colors = [
            '#FF0000', '#0000FF', '#00FF00', '#B22222', '#FF7F50',
            '#9ACD32', '#FF4500', '#2E8B57', '#DAA520', '#D2691E',
            '#5F9EA0', '#1E90FF', '#FF69B4', '#8A2BE2', '#00FF7F'
        ];
        
        // Utiliser le nom d'utilisateur pour générer un index cohérent
        const hash = username.toLowerCase().split('').reduce((a, b) => {
            a = ((a << 5) - a) + b.charCodeAt(0);
            return a & a;
        }, 0);
        
        return colors[Math.abs(hash) % colors.length];
    }

    /**
     * Rend l'interface du chat global
     */
    renderGlobalChatInterface() {
        // Ajouter la classe global-chat au conteneur pour que les styles CSS s'appliquent
        this.chatContainer.className = 'global-chat';
        
        // Si on a du contenu sauvegardé, le restaurer
        if (this.savedGlobalChatContent) {
            this.chatContainer.innerHTML = this.savedGlobalChatContent;
            this.savedGlobalChatContent = null; // Nettoyer après restauration
            applyTranslations(this.chatContainer);
            
            // Réactiver les event listeners
            this.setupEventListeners();
            this.setupScrollListener();
            return;
        }
        
        // Sinon, créer une nouvelle interface simplifiée
        this.chatContainer.innerHTML = `
            <div class="global-chat-container">
                <div class="global-chat-messages" id="global-chat-messages">
                    <div class="chat-welcome-message">
                        <i class="fas fa-globe"></i>
                        <h3 data-i18n="chat.globalEnabled">${t('chat.globalEnabled')}</h3>
                        <p data-i18n="chat.globalDescription">${t('chat.globalDescription')}</p>
                        <div class="streamers-list">
                            <div class="streamers-names">
                                ${Array.from(this.activeStreams.values()).map(streamer => `
                                    <span class="streamer-tag">${streamer.display_name}</span>
                                `).join('')}
                            </div>
                        </div>
                        <p class="chat-info" data-i18n="chat.realtime">${t('chat.realtime')}</p>
                    </div>
                </div>
                <div class="chat-resizer" id="chat-resizer">
                    <div class="resizer-handle"></div>
                </div>
                <div class="global-chat-streamers" id="global-chat-streamers">
                    <div class="streamers-legend">
                        <span data-i18n="chat.activeStreamers">${t('chat.activeStreamers')}</span>
                        <div class="streamers-list">
                            ${Array.from(this.activeStreams.values()).map(streamer => `
                                <div class="streamer-indicator" data-streamer-id="${streamer.id}" style="border-color: ${streamer.color}">
                                    <img src="${streamer.profile_image_url}" alt="${streamer.display_name}" class="streamer-avatar">
                                    <div class="separator"></div>
                                    <span class="streamer-name">${streamer.display_name}</span>
                                </div>
                            `).join('')}
                        </div>
                    </div>
                </div>
            </div>
        `;

        // Ajouter les event listeners
        this.setupEventListeners();
        this.setupScrollListener();
        this.setupChatResizer();
    }

    /**
     * Configure les event listeners
     */
    setupEventListeners() {
        // Ajouter les événements de clic pour les indicateurs de streamers
        const streamerIndicators = this.chatContainer.querySelectorAll('.streamer-indicator');
        streamerIndicators.forEach(indicator => {
            indicator.addEventListener('click', (e) => {
                const streamerId = e.currentTarget.dataset.streamerId;
                if (streamerId) {
                    this.toggleStreamerVisibility(streamerId);
                }
            });
        });
    }
    
    /**
     * Bascule vers le mode de chat individuel (appelé depuis la sidebar)
     */
    switchToIndividualMode() {
        // Sauvegarder le contenu du chat global seulement s'il est actif et a du contenu
        if (this.isGlobalMode && this.chatContainer.innerHTML.trim() !== '') {
            this.savedGlobalChatContent = this.chatContainer.innerHTML;
        }
        
        // Désactiver le mode global
        this.disableGlobalChat();
        
        // Nettoyer complètement le conteneur
        this.chatContainer.innerHTML = '';
        this.chatContainer.className = '';
        
        // Détecter le streamer actuellement sélectionné
        const selectedStreamer = this.getSelectedStreamer();
        
        if (selectedStreamer) {
            // Afficher directement l'iframe du chat Twitch
            this.showTwitchChat(selectedStreamer.login);
        } else {
            // Aucun streamer sélectionné, afficher le message par défaut
            if (window.app && typeof window.app.showDefaultChatMessage === 'function') {
                window.app.showDefaultChatMessage();
            } else {
                // Fallback si app n'est pas disponible
                this.chatContainer.innerHTML = `
                    <div class="default-chat-message">
                        <img src="/static/images/logo.png" alt="MultiStream Logo" class="chat-logo">
                        <h3>${t('chat.individualMode')}</h3>
                        <p>${t('chat.clickHint')}</p>
                        <p>${t('chat.globalHint')}</p>
                    </div>
                `;
            }
        }
    }

    /**
     * Détecte le streamer actuellement sélectionné
     */
    getSelectedStreamer() {
        // Chercher dans les streamers actifs ou dans la liste des streamers
        const streamerItems = document.querySelectorAll('.streamer-item');
        
        // Prendre le premier streamer en live comme sélection par défaut
        for (const item of streamerItems) {
            const streamerId = item.dataset.streamerId;
            const streamer = Array.from(this.activeStreams.values()).find(s => s.id === streamerId);
            if (streamer) {
                return streamer;
            }
        }
        
        // Si aucun streamer actif, prendre le premier de la liste
        if (streamerItems.length > 0) {
            const firstItem = streamerItems[0];
            const nameEl = firstItem.querySelector('.streamer-name');
            if (nameEl) {
                return {
                    login: nameEl.textContent.toLowerCase().replace(/\s+/g, ''),
                    display_name: nameEl.textContent
                };
            }
        }
        
        return null;
    }
    
    /**
     * Affiche l'iframe du chat Twitch natif
     */
    showTwitchChat(streamerLogin) {
        this.chatContainer.innerHTML = '';
        this.chatContainer.className = '';
        
        const iframe = document.createElement('iframe');
        iframe.className = 'chat-embed';
        iframe.src = createTwitchChatUrl(streamerLogin);
        iframe.style.width = '100%';
        iframe.style.height = '100%';
        iframe.style.border = 'none';
        
        this.chatContainer.appendChild(iframe);
    }

    /**
     * Se connecte aux chats de tous les streams actifs
     * Note: Pour une vraie implémentation, il faudrait utiliser l'API Twitch IRC ou EventSub
     * Ici on simule avec des messages d'exemple
     */
    connectToChats() {
        // Simulation de connexion aux chats
        this.simulateMessages();
    }

    /**
     * Se déconnecte de tous les chats
     */
    disconnectFromChats() {
        this.chatConnections.forEach(connection => {
            if (connection && connection.close) {
                connection.close();
            }
        });
        this.chatConnections.clear();
    }

    async fetchWithTimeout(url, timeoutMs = 5000) {
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

        try {
            return await fetch(url, { signal: controller.signal });
        } finally {
            window.clearTimeout(timeoutId);
        }
    }

    /**
     * Récupère les emotes BetterTTV globales
     */
    async fetchGlobalBTTVEmotes() {
        if (this.globalBTTVEmotes) {
            return this.globalBTTVEmotes;
        }

        try {
            const response = await this.fetchWithTimeout('https://api.betterttv.net/3/cached/emotes/global');
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const emotes = await response.json();
            
            this.globalBTTVEmotes = {};
            emotes.forEach(emote => {
                this.globalBTTVEmotes[emote.code] = {
                    id: emote.id,
                    url: `https://cdn.betterttv.net/emote/${emote.id}/1x`,
                    url2x: `https://cdn.betterttv.net/emote/${emote.id}/2x`,
                    type: 'bttv'
                };
            });
            
            return this.globalBTTVEmotes;
        } catch (error) {
            console.warn('Impossible de récupérer les emotes BetterTTV globales:', error.message);
            this.globalBTTVEmotes = {};
            return {};
        }
    }

    /**
     * Récupère les emotes BetterTTV d'un canal
     */
    async fetchChannelBTTVEmotes(channelName) {
        if (this.bttvEmotesCache.has(channelName)) {
            return this.bttvEmotesCache.get(channelName);
        }

        try {
            const broadcasterId = await this.getBroadcasterIdFromChannelName(channelName);
            if (!broadcasterId) throw new Error(`Impossible de trouver l'ID pour le canal ${channelName}`);

            const response = await this.fetchWithTimeout(`https://api.betterttv.net/3/cached/users/twitch/${broadcasterId}`);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const data = await response.json();
            
            const channelEmotes = {};
            
            // Emotes du canal
            if (data.channelEmotes) {
                data.channelEmotes.forEach(emote => {
                    channelEmotes[emote.code] = {
                        id: emote.id,
                        url: `https://cdn.betterttv.net/emote/${emote.id}/1x`,
                        url2x: `https://cdn.betterttv.net/emote/${emote.id}/2x`,
                        type: 'bttv'
                    };
                });
            }
            
            // Emotes partagées
            if (data.sharedEmotes) {
                data.sharedEmotes.forEach(emote => {
                    channelEmotes[emote.code] = {
                        id: emote.id,
                        url: `https://cdn.betterttv.net/emote/${emote.id}/1x`,
                        url2x: `https://cdn.betterttv.net/emote/${emote.id}/2x`,
                        type: 'bttv'
                    };
                });
            }
            
            this.bttvEmotesCache.set(channelName, channelEmotes);
            return channelEmotes;
        } catch (error) {
            console.warn(`Impossible de récupérer les emotes BetterTTV pour ${channelName}:`, error.message);
            this.bttvEmotesCache.set(channelName, {});
            return {};
        }
    }

    /**
     * Récupère les emotes FrankerFaceZ globales
     */
    async fetchGlobalFFZEmotes() {
        if (this.globalFFZEmotes) {
            return this.globalFFZEmotes;
        }

        try {
            const response = await this.fetchWithTimeout('https://api.frankerfacez.com/v1/set/global');
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const data = await response.json();
            
            this.globalFFZEmotes = {};
            
            if (data.sets) {
                Object.values(data.sets).forEach(set => {
                    if (set.emoticons) {
                        set.emoticons.forEach(emote => {
                            this.globalFFZEmotes[emote.name] = {
                                id: emote.id,
                                url: `https://cdn.frankerfacez.com/emoticon/${emote.id}/1`,
                                url2x: `https://cdn.frankerfacez.com/emoticon/${emote.id}/2`,
                                type: 'ffz'
                            };
                        });
                    }
                });
            }
            
            return this.globalFFZEmotes;
        } catch (error) {
            console.warn('Impossible de récupérer les emotes FrankerFaceZ globales:', error.message);
            this.globalFFZEmotes = {};
            return {};
        }
    }

    /**
     * Récupère les emotes FrankerFaceZ d'un canal
     */
    async fetchChannelFFZEmotes(channelName) {
        if (this.ffzEmotesCache.has(channelName)) {
            return this.ffzEmotesCache.get(channelName);
        }

        try {
            const response = await this.fetchWithTimeout(`https://api.frankerfacez.com/v1/room/${channelName}`);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const data = await response.json();
            
            const channelEmotes = {};
            
            if (data.sets) {
                Object.values(data.sets).forEach(set => {
                    if (set.emoticons) {
                        set.emoticons.forEach(emote => {
                            channelEmotes[emote.name] = {
                                id: emote.id,
                                url: `https://cdn.frankerfacez.com/emoticon/${emote.id}/1`,
                                url2x: `https://cdn.frankerfacez.com/emoticon/${emote.id}/2`,
                                type: 'ffz'
                            };
                        });
                    }
                });
            }
            
            this.ffzEmotesCache.set(channelName, channelEmotes);
            return channelEmotes;
        } catch (error) {
            console.warn(`Impossible de récupérer les emotes FrankerFaceZ pour ${channelName}:`, error.message);
            this.ffzEmotesCache.set(channelName, {});
            return {};
        }
    }

    parseSevenTVEmoteSet(emoteSet) {
        const emotes = {};

        for (const activeEmote of emoteSet?.emotes || []) {
            const emote = activeEmote.data || activeEmote;
            const name = activeEmote.name || emote.name;
            const host = emote.host;
            if (!name || !host?.url) continue;

            const hostUrl = host.url.startsWith('//') ? `https:${host.url}` : host.url;
            const files = host.files || [];
            const file1x = files.find(file => file.name === '1x.webp')?.name || '1x.webp';
            const file2x = files.find(file => file.name === '2x.webp')?.name || file1x;

            emotes[name] = {
                id: activeEmote.id || emote.id,
                url: `${hostUrl}/${file1x}`,
                url2x: `${hostUrl}/${file2x}`,
                type: 'seventv',
                animated: Boolean(emote.animated),
            };
        }

        return emotes;
    }

    async fetchGlobalSevenTVEmotes() {
        if (this.globalSevenTVEmotes) return this.globalSevenTVEmotes;

        try {
            const response = await this.fetchWithTimeout('https://7tv.io/v3/emote-sets/global');
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            this.globalSevenTVEmotes = this.parseSevenTVEmoteSet(await response.json());
        } catch (error) {
            console.warn('Impossible de récupérer les emotes 7TV globales:', error.message);
            this.globalSevenTVEmotes = {};
        }

        return this.globalSevenTVEmotes;
    }

    async fetchChannelSevenTVEmotes(channelName) {
        if (this.sevenTVEmotesCache.has(channelName)) return this.sevenTVEmotesCache.get(channelName);

        try {
            const broadcasterId = await this.getBroadcasterIdFromChannelName(channelName);
            if (!broadcasterId) throw new Error(`Impossible de trouver l'ID pour le canal ${channelName}`);

            const response = await this.fetchWithTimeout(`https://7tv.io/v3/users/twitch/${broadcasterId}`);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const user = await response.json();
            const emotes = this.parseSevenTVEmoteSet(user.emote_set);
            this.sevenTVEmotesCache.set(channelName, emotes);
            return emotes;
        } catch (error) {
            console.warn(`Impossible de récupérer les emotes 7TV pour ${channelName}:`, error.message);
            this.sevenTVEmotesCache.set(channelName, {});
            return {};
        }
    }

    /**
     * Traite les badges avec l'API officielle Twitch
     */
    async processBadges(badgeTags, channelName) {
        const badges = {};
        
        if (!badgeTags) return badges;
        
        // Enrichir avec les données officielles si possible
        try {
            // Cache des badges globaux
            if (!this.globalBadgesCache) {
                this.globalBadgesCache = await this.fetchGlobalBadges();
            }
            
            // Cache des badges de canal
            if (!this.channelBadgesCache) {
                this.channelBadgesCache = new Map();
            }
            
            if (!this.channelBadgesCache.has(channelName)) {
                const channelBadges = await this.fetchChannelBadges(channelName);
                this.channelBadgesCache.set(channelName, channelBadges);
            }
            
            // Traiter chaque badge avec sa version
            for (const [badgeType, badgeVersion] of Object.entries(badgeTags)) {
                const version = badgeVersion.toString();
                
                // Chercher d'abord dans les badges du canal, puis dans les globaux
                const channelBadges = this.channelBadgesCache.get(channelName);
                const globalBadges = this.globalBadgesCache;
                
                let badgeData = null;
                
                // Priorité aux badges du canal
                if (channelBadges?.[badgeType]?.[version]) {
                    badgeData = channelBadges[badgeType][version];
                } else if (globalBadges?.[badgeType]?.[version]) {
                    badgeData = globalBadges[badgeType][version];
                }
                
                if (badgeData) {
                    badges[badgeType] = {
                        version: version,
                        imageUrl: badgeData.image_url_1x,
                        imageUrl2x: badgeData.image_url_2x,
                        imageUrl4x: badgeData.image_url_4x,
                        title: badgeData.title || badgeType,
                        description: badgeData.description || ''
                    };
                } else {
                    // Fallback si le badge n'est pas trouvé
                    badges[badgeType] = {
                        version: version,
                        imageUrl: null,
                        title: badgeType
                    };
                }
            }
        } catch (error) {
            console.warn('Erreur lors de la récupération des badges:', error);
            // En cas d'erreur, créer des badges basiques
            for (const [badgeType, badgeVersion] of Object.entries(badgeTags)) {
                badges[badgeType] = {
                    version: badgeVersion.toString(),
                    imageUrl: null,
                    title: badgeType
                };
            }
        }
        
        return badges;
    }

    /**
     * Récupère les badges globaux Twitch via l'API Helix
     */
    async fetchGlobalBadges() {
        try {
            const response = await this.fetchWithTimeout('/api/twitch/chat/badges');
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            
            // Transformer les données de l'API Twitch en format utilisable
            const badges = {};
            if (data.data) {
                data.data.forEach(badgeSet => {
                    badges[badgeSet.set_id] = {};
                    badgeSet.versions.forEach(version => {
                        badges[badgeSet.set_id][version.id] = {
                            image_url_1x: version.image_url_1x,
                            image_url_2x: version.image_url_2x,
                            image_url_4x: version.image_url_4x,
                            title: version.title,
                            description: version.description
                        };
                    });
                });
            }
            
            return badges;
        } catch (error) {
            console.warn('Impossible de récupérer les badges globaux:', error);
            
            // Badges globaux de base avec emojis comme fallback
            const fallbackBadges = {
                'staff': {
                    '1': {
                        image_url_1x: null,
                        title: 'Staff Twitch',
                        description: 'Membre du staff Twitch',
                        emoji: '🔧'
                    }
                },
                'admin': {
                    '1': {
                        image_url_1x: null,
                        title: 'Admin',
                        description: 'Administrateur Twitch',
                        emoji: '👑'
                    }
                },
                'global_mod': {
                    '1': {
                        image_url_1x: null,
                        title: 'Global Moderator',
                        description: 'Modérateur global',
                        emoji: '🌍'
                    }
                },
                'premium': {
                    '1': {
                        image_url_1x: null,
                        title: 'Prime Gaming',
                        description: 'Abonné Prime Gaming',
                        emoji: '⚡'
                    }
                },
                'partner': {
                    '1': {
                        image_url_1x: null,
                        title: 'Verified',
                        description: 'Compte vérifié',
                        emoji: '✅'
                    }
                }
            };
            
            return fallbackBadges;
        }
    }

    /**
     * Obtient l'ID du broadcaster à partir du nom du canal
     */
    async getBroadcasterIdFromChannelName(channelName) {
        try {
            // Chercher dans les streamers actifs d'abord
            for (const streamer of this.activeStreams.values()) {
                if (streamer.login && streamer.login.toLowerCase() === channelName.toLowerCase()) {
                    return streamer.id;
                }
            }
            
            // Si pas trouvé dans les streamers actifs, chercher dans l'application principale
            if (window.app && window.app.streamers) {
                for (const streamer of window.app.streamers.values()) {
                    if (streamer.login && streamer.login.toLowerCase() === channelName.toLowerCase()) {
                        return streamer.id;
                    }
                }
            }
            
            return null;
        } catch (error) {
            console.warn(`Erreur lors de la récupération de l'ID du broadcaster pour ${channelName}:`, error);
            return null;
        }
    }

    /**
     * Récupère les badges d'un canal spécifique
     */
    async fetchChannelBadges(channelName) {
        // Vérifier le cache d'abord
        if (this.channelBadgesCache && this.channelBadgesCache.has(channelName)) {
            return this.channelBadgesCache.get(channelName);
        }

        try {
            // Obtenir l'ID du broadcaster à partir du nom du canal
            const broadcasterId = await this.getBroadcasterIdFromChannelName(channelName);
            
            if (!broadcasterId) {
                throw new Error(`Impossible de trouver l'ID pour le canal ${channelName}`);
            }
            
            const response = await this.fetchWithTimeout(`/api/twitch/chat/badges?broadcaster_id=${broadcasterId}`);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            
            // Transformer les données de l'API Twitch en format utilisable
            const badges = {};
            if (data.data) {
                data.data.forEach(badgeSet => {
                    badges[badgeSet.set_id] = {};
                    badgeSet.versions.forEach(version => {
                        badges[badgeSet.set_id][version.id] = {
                            image_url_1x: version.image_url_1x,
                            image_url_2x: version.image_url_2x,
                            image_url_4x: version.image_url_4x,
                            title: version.title,
                            description: version.description
                        };
                    });
                });
            }
            
            // Mettre en cache le résultat
            if (!this.channelBadgesCache) {
                this.channelBadgesCache = new Map();
            }
            this.channelBadgesCache.set(channelName, badges);
            
            return badges;
        } catch (error) {
            console.warn(`Impossible de récupérer les badges du canal ${channelName}:`, error.message);
            
            // Badges de canal de base avec emojis comme fallback
            const fallbackBadges = {
                'broadcaster': {
                    '1': {
                        image_url_1x: null,
                        title: 'Broadcaster',
                        description: 'Diffuseur de la chaîne',
                        emoji: '📺'
                    }
                },
                'moderator': {
                    '1': {
                        image_url_1x: null,
                        title: 'Moderator',
                        description: 'Modérateur de la chaîne',
                        emoji: '🗡️'
                    }
                },
                'vip': {
                    '1': {
                        image_url_1x: null,
                        title: 'VIP',
                        description: 'VIP de la chaîne',
                        emoji: '💎'
                    }
                },
                'subscriber': {
                    '1': {
                        image_url_1x: null,
                        title: 'Subscriber',
                        description: 'Abonné à la chaîne',
                        emoji: '⭐'
                    },
                    '3': {
                        image_url_1x: null,
                        title: '3-Month Subscriber',
                        description: 'Abonné depuis 3 mois',
                        emoji: '⭐'
                    },
                    '6': {
                        image_url_1x: null,
                        title: '6-Month Subscriber',
                        description: 'Abonné depuis 6 mois',
                        emoji: '⭐'
                    },
                    '12': {
                        image_url_1x: null,
                        title: '1-Year Subscriber',
                        description: 'Abonné depuis 1 an',
                        emoji: '⭐'
                    }
                },
                'founder': {
                    '0': {
                        image_url_1x: null,
                        title: 'Founder',
                        description: 'Fondateur de la chaîne',
                        emoji: '🏆'
                    }
                },
                'bits': {
                    '1': {
                        image_url_1x: null,
                        title: 'Bits',
                        description: 'Supporter avec des Bits',
                        emoji: '💰'
                    }
                },
                'sub-gifter': {
                    '1': {
                        image_url_1x: null,
                        title: 'Sub Gifter',
                        description: 'Donateur d\'abonnements',
                        emoji: '🎁'
                    }
                }
            };
            
            // Mettre en cache le résultat fallback
            if (!this.channelBadgesCache) {
                this.channelBadgesCache = new Map();
            }
            this.channelBadgesCache.set(channelName, fallbackBadges);
            
            return fallbackBadges;
        }
    }

    /**
     * Ajoute un message au chat global
     */
    async addMessage(streamerId, username, message, userColor = null, badges = {}, streamerProfileImage = null, channelName = null, twitchEmotes = null, twitchGifs = null) {
        if (!this.isGlobalMode) return;
        
        const streamer = this.activeStreams.get(streamerId);
        if (!streamer) return;

        const chatMessage = {
            id: Date.now() + Math.random(),
            streamerId,
            streamer,
            streamerName: streamer.display_name || streamer.login,
            streamerColor: streamer.color,
            streamerProfileImage: streamerProfileImage || streamer.profile_image_url,
            username,
            message,
            userColor: userColor || this.generateUserColor(username),
            badges: badges || {},
            channelName: channelName || streamerId,
            twitchEmotes,
            twitchGifs,
            timestamp: new Date()
        };

        this.chatMessages.push(chatMessage);
        
        // Limiter le nombre de messages avec nettoyage optimisé
        if (this.chatMessages.length > this.maxMessages) {
            const messagesToRemove = this.chatMessages.length - this.maxMessages;
            // Supprimer les anciens messages du DOM avant de les retirer du cache
            const messagesContainer = document.getElementById('global-chat-messages');
            if (messagesContainer) {
                const messageElements = messagesContainer.querySelectorAll('.chat-message');
                for (let i = 0; i < messagesToRemove && i < messageElements.length; i++) {
                    messageElements[i].remove();
                }
            }
            
            // Nettoyer les références des anciens messages
            const removedMessages = this.chatMessages.splice(0, messagesToRemove);
            removedMessages.forEach(msg => {
                // Nettoyer les références pour aider le garbage collector
                msg.streamer = null;
                msg.badges = null;
            });
        }

        await this.renderMessage(chatMessage);
    }

    /**
     * Rend un message dans le chat
     */
    async renderMessage(chatMessage) {
        const messagesContainer = document.getElementById('global-chat-messages');
        if (!messagesContainer) return;

        // Supprimer le message de bienvenue s'il existe
        const welcomeMessage = messagesContainer.querySelector('.chat-welcome-message');
        if (welcomeMessage) {
            welcomeMessage.remove();
        }

        const badgesFragment = this.renderBadges(chatMessage.badges || {});
        
        // Parser les emotes AVANT de créer le HTML
        const parsedMessage = await this.parseEmotes(
            chatMessage.message,
            chatMessage.channelName,
            chatMessage.twitchEmotes,
            chatMessage.twitchGifs,
        );

        const messageElement = document.createElement('div');
        messageElement.className = 'chat-message';
        messageElement.dataset.streamerId = chatMessage.streamerId;
        
        // Cacher le message si le streamer est dans la liste des cachés
        if (this.hiddenStreamers.has(chatMessage.streamerId)) {
            messageElement.style.display = 'none';
        }
        
        const messageHeader = document.createElement('div');
        messageHeader.className = 'message-header';

        const streamerInfo = document.createElement('div');
        streamerInfo.className = 'streamer-info';
        streamerInfo.style.borderLeftColor = chatMessage.streamerColor || '#9146ff';

        if (chatMessage.streamerProfileImage) {
            const streamerAvatar = document.createElement('img');
            streamerAvatar.className = 'streamer-avatar';
            streamerAvatar.src = chatMessage.streamerProfileImage;
            streamerAvatar.alt = '';
            streamerAvatar.loading = 'lazy';
            streamerAvatar.decoding = 'async';
            streamerAvatar.addEventListener('error', () => streamerAvatar.remove(), { once: true });
            streamerInfo.appendChild(streamerAvatar);
        }

        const streamerName = document.createElement('span');
        streamerName.className = 'streamer-name';
        streamerName.textContent = chatMessage.streamerName || chatMessage.streamerId;
        streamerInfo.appendChild(streamerName);

        const messageTime = document.createElement('span');
        messageTime.className = 'message-time';
        messageTime.textContent = this.formatTime(chatMessage.timestamp);
        messageHeader.append(streamerInfo, messageTime);

        const messageContent = document.createElement('div');
        messageContent.className = 'message-content';
        const username = document.createElement('span');
        username.className = 'username';
        username.style.color = chatMessage.userColor;
        username.textContent = `${chatMessage.username}:`;
        const messageText = document.createElement('span');
        messageText.className = 'message-text';
        messageContent.append(username, document.createTextNode(' '), messageText);
        messageElement.append(messageHeader, messageContent);

        messageContent.insertBefore(badgesFragment, messageContent.firstChild);

        // Insérer le message parsé avec les emotes
        renderChatMessage(messageText, parsedMessage);

        messagesContainer.appendChild(messageElement);
        
        // Scroll automatique seulement si l'utilisateur est en bas
        this.handleAutoScroll(messagesContainer);
    }

    /**
     * Rend les badges d'un utilisateur avec les vraies images Twitch
     */
    renderBadges(badges) {
        const fragment = document.createDocumentFragment();
        if (!badges || Object.keys(badges).length === 0) {
            return fragment;
        }
        
        // Ordre de priorité pour l'affichage des badges
        const badgeOrder = ['broadcaster', 'moderator', 'vip', 'subscriber', 'premium', 'partner', 'staff', 'admin', 'global_mod', 'founder', 'artist-badge', 'bits', 'sub-gifter'];
        
        // Fallback emojis pour les badges sans image
        const badgeFallbacks = {
            'broadcaster': '📺',
            'moderator': '🗡️',
            'vip': '💎',
            'subscriber': '⭐',
            'premium': '⚡',
            'partner': '✅',
            'staff': '🔧',
            'admin': '👑',
            'global_mod': '🌍',
            'founder': '🏆',
            'artist-badge': '🎨',
            'bits': '💰',
            'sub-gifter': '🎁'
        };

        // Trier les badges selon l'ordre de priorité
        const sortedBadges = Object.entries(badges).sort(([a], [b]) => {
            const indexA = badgeOrder.indexOf(a);
            const indexB = badgeOrder.indexOf(b);
            if (indexA === -1 && indexB === -1) return 0;
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
        });

        for (const [badgeType, badgeData] of sortedBadges) {
            const title = badgeData.title || badgeType;
            const description = badgeData.description || title;
            const fallbackEmoji = badgeFallbacks[badgeType] || '🏷️';
            const safeBadgeType = badgeType.replace(/[^a-z0-9_-]/gi, '');
            let badgeElement;
            
            if (badgeData.imageUrl) {
                const srcset = [];
                if (badgeData.imageUrl) srcset.push(`${badgeData.imageUrl} 1x`);
                if (badgeData.imageUrl2x) srcset.push(`${badgeData.imageUrl2x} 2x`);
                if (badgeData.imageUrl4x) srcset.push(`${badgeData.imageUrl4x} 4x`);

                badgeElement = document.createElement('img');
                badgeElement.src = badgeData.imageUrl;
                if (srcset.length > 0) badgeElement.srcset = srcset.join(', ');
                badgeElement.alt = title;
                badgeElement.loading = 'lazy';
                badgeElement.decoding = 'async';
                badgeElement.addEventListener('error', () => {
                    badgeElement.replaceWith(document.createTextNode(badgeData.emoji || fallbackEmoji));
                }, { once: true });
            } else {
                badgeElement = document.createElement('span');
                badgeElement.textContent = badgeData.emoji || fallbackEmoji;
            }

            badgeElement.className = `chat-badge badge-${safeBadgeType}`;
            badgeElement.title = description;
            fragment.appendChild(badgeElement);
        }

        fragment.appendChild(document.createTextNode(' '));
        return fragment;
    }

    /**
     * Parse les emotes dans un message avec support des emotes tierces
     */
    async parseEmotes(message, channelName = null, twitchEmotes = null, twitchGifs = null) {
        const allEmotes = await this.getAllEmotes(channelName);
        return parseChatMessage(message, allEmotes, twitchEmotes, twitchGifs);
    }

    /**
     * Récupère les emotes globales Twitch
     */
    async fetchTwitchGlobalEmotes() {
        if (this.twitchGlobalEmotes) {
            return this.twitchGlobalEmotes;
        }

        try {
            const response = await this.fetchWithTimeout('/api/twitch/chat/emotes/global');
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            
            const data = await response.json();
            const globalEmotes = {};
            
            if (data.data) {
                data.data.forEach(emote => {
                    // Détecter si l'emote est animée et utiliser l'URL appropriée
                    const isAnimated = emote.format && emote.format.includes('animated');
                    const baseUrl = isAnimated ? 
                        `https://static-cdn.jtvnw.net/emoticons/v2/${emote.id}/animated/dark/1.0` :
                        emote.images.url_1x;
                    const baseUrl2x = isAnimated ? 
                        `https://static-cdn.jtvnw.net/emoticons/v2/${emote.id}/animated/dark/2.0` :
                        (emote.images.url_2x || emote.images.url_4x);
                    
                    globalEmotes[emote.name] = {
                        id: emote.id,
                        url: baseUrl,
                        url2x: baseUrl2x,
                        type: 'twitch-global',
                        animated: isAnimated
                    };
                });
            }
            
            this.twitchGlobalEmotes = globalEmotes;
            return globalEmotes;
        } catch (error) {
            console.warn('Impossible de récupérer les emotes globales Twitch:', error.message);
            this.twitchGlobalEmotes = {};
            return {};
        }
    }

    /**
     * Récupère les emotes Twitch personnalisées d'un canal
     */
    async fetchTwitchChannelEmotes(channelName) {
        if (this.twitchChannelEmotesCache.has(channelName)) {
            return this.twitchChannelEmotesCache.get(channelName);
        }

        try {
            const broadcasterId = await this.getBroadcasterIdFromChannelName(channelName);
            if (!broadcasterId) {
                this.twitchChannelEmotesCache.set(channelName, {});
                return {};
            }

            const response = await this.fetchWithTimeout(`/api/twitch/chat/emotes?broadcaster_id=${broadcasterId}`);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            
            const data = await response.json();
            const channelEmotes = {};
            
            if (data.data) {
                data.data.forEach(emote => {
                    // Détecter si l'emote est animée et utiliser l'URL appropriée
                    const isAnimated = emote.format && emote.format.includes('animated');
                    const baseUrl = isAnimated ? 
                        `https://static-cdn.jtvnw.net/emoticons/v2/${emote.id}/animated/dark/1.0` :
                        emote.images.url_1x;
                    const baseUrl2x = isAnimated ? 
                        `https://static-cdn.jtvnw.net/emoticons/v2/${emote.id}/animated/dark/2.0` :
                        (emote.images.url_2x || emote.images.url_4x);
                    
                    channelEmotes[emote.name] = {
                        id: emote.id,
                        url: baseUrl,
                        url2x: baseUrl2x,
                        type: 'twitch-custom',
                        animated: isAnimated
                    };
                });
            }
            
            this.twitchChannelEmotesCache.set(channelName, channelEmotes);
            return channelEmotes;
        } catch (error) {
            console.warn(`Impossible de récupérer les emotes Twitch pour ${channelName}:`, error.message);
            this.twitchChannelEmotesCache.set(channelName, {});
            return {};
        }
    }

    /**
     * Récupère toutes les emotes disponibles (globales + canal)
     */
    async getAllEmotes(channelName = null) {
        const cacheKey = channelName || 'global';
        
        // Vérifier si les emotes sont déjà en cache et pas expirées
        if (this.allEmotesCache.has(cacheKey)) {
            const timestamp = this.cacheTimestamps.get(cacheKey);
            if (timestamp && (Date.now() - timestamp) < this.cacheExpiration) {
                return this.allEmotesCache.get(cacheKey);
            } else {
                // Cache expiré, le supprimer
                this.allEmotesCache.delete(cacheKey);
                this.cacheTimestamps.delete(cacheKey);
            }
        }
        
        const allEmotes = {};
        
        try {
            // Charger les emotes globales une seule fois
            if (!this.globalEmotesLoaded) {
                await this.loadGlobalEmotes();
                this.globalEmotesLoaded = true;
            }
            
            // Ajouter les emotes globales (déjà en cache)
            if (this.twitchGlobalEmotes) {
                Object.assign(allEmotes, this.twitchGlobalEmotes);
            }
            if (this.globalBTTVEmotes) {
                Object.assign(allEmotes, this.globalBTTVEmotes);
            }
            if (this.globalFFZEmotes) {
                Object.assign(allEmotes, this.globalFFZEmotes);
            }
            if (this.globalSevenTVEmotes) {
                Object.assign(allEmotes, this.globalSevenTVEmotes);
            }
            
            // Si un canal est spécifié, récupérer ses emotes
            if (channelName) {
                const channelProviders = await Promise.allSettled([
                    this.fetchTwitchChannelEmotes(channelName),
                    this.fetchChannelBTTVEmotes(channelName),
                    this.fetchChannelFFZEmotes(channelName),
                    this.fetchChannelSevenTVEmotes(channelName),
                ]);

                channelProviders.forEach((result) => {
                    if (result.status === 'fulfilled') Object.assign(allEmotes, result.value);
                });
            }
            
            // Mettre en cache le résultat avec timestamp
            this.allEmotesCache.set(cacheKey, allEmotes);
            this.cacheTimestamps.set(cacheKey, Date.now());
            
        } catch (error) {
            console.warn('Erreur lors de la récupération des emotes:', error);
        }
        
        return allEmotes;
    }

    /**
     * Charge toutes les emotes globales en une seule fois
     */
    async loadGlobalEmotes() {
        try {
            // Charger toutes les emotes globales en parallèle
            await Promise.allSettled([
                this.fetchTwitchGlobalEmotes(),
                this.fetchGlobalBTTVEmotes(),
                this.fetchGlobalFFZEmotes(),
                this.fetchGlobalSevenTVEmotes(),
            ]);
            
            // Les résultats sont déjà stockés dans les propriétés de classe
            console.log('✅ Emotes globales chargées avec succès');
        } catch (error) {
            console.warn('⚠️ Erreur lors du chargement des emotes globales:', error);
        }
    }

    /**
     * Précharge les emotes globales pour améliorer les performances
     */
    async preloadGlobalEmotes() {
        if (!this.globalEmotesLoaded) {
            await this.loadGlobalEmotes();
            this.globalEmotesLoaded = true;
        }
    }

    /**
     * Nettoie le cache des emotes expirées
     */
    cleanExpiredCache() {
        const now = Date.now();
        for (const [key, timestamp] of this.cacheTimestamps.entries()) {
            if ((now - timestamp) >= this.cacheExpiration) {
                this.allEmotesCache.delete(key);
                this.cacheTimestamps.delete(key);
            }
        }
    }

    /**
     * Vide complètement le cache des emotes
     */
    clearEmotesCache() {
        this.allEmotesCache.clear();
        this.cacheTimestamps.clear();
        this.bttvEmotesCache.clear();
        this.ffzEmotesCache.clear();
        this.sevenTVEmotesCache.clear();
        this.twitchChannelEmotesCache.clear();
        this.globalBTTVEmotes = null;
        this.globalFFZEmotes = null;
        this.globalSevenTVEmotes = null;
        this.twitchGlobalEmotes = null;
        this.globalEmotesLoaded = false;
        console.log('🧹 Cache des emotes vidé');
    }

    /**
     * Nettoie les anciens messages pour éviter l'accumulation en mémoire
     */
    cleanOldMessages() {
        if (!this.isGlobalMode) return;
        
        const now = Date.now();
        const messagesContainer = document.getElementById('global-chat-messages');
        
        // Nettoyer les messages en mémoire (par âge)
        const initialLength = this.chatMessages.length;
        this.chatMessages = this.chatMessages.filter(message => {
            const messageAge = now - new Date(message.timestamp).getTime();
            return messageAge < this.maxMessageAge;
        });
        
        // Nettoyer les éléments DOM si trop nombreux
        if (messagesContainer) {
            const messageElements = messagesContainer.querySelectorAll('.chat-message');
            const excessMessages = messageElements.length - this.maxDOMMessages;
            
            if (excessMessages > 0) {
                // Supprimer les anciens messages du DOM
                for (let i = 0; i < excessMessages; i++) {
                    if (messageElements[i]) {
                        messageElements[i].remove();
                    }
                }
            }
        }
        
        // Log du nettoyage si des messages ont été supprimés
        const cleanedCount = initialLength - this.chatMessages.length;
        if (cleanedCount > 0) {
            console.log(`🧹 ${cleanedCount} anciens messages nettoyés du cache`);
        }
    }

    /**
     * Vide complètement tous les messages du chat
     */
    clearAllMessages() {
        // Vider le cache des messages
        this.chatMessages = [];
        
        // Vider le DOM
        const messagesContainer = document.getElementById('global-chat-messages');
        if (messagesContainer) {
            // Garder seulement le message de bienvenue s'il existe
            const welcomeMessage = messagesContainer.querySelector('.chat-welcome-message');
            messagesContainer.innerHTML = '';
            if (welcomeMessage) {
                messagesContainer.appendChild(welcomeMessage);
            }
        }
        
        console.log('🧹 Tous les messages du chat global ont été supprimés');
    }

    /**
     * Méthode publique pour forcer le nettoyage manuel
     * Peut être appelée depuis l'interface ou la console
     */
    forceCleanup() {
        console.log('🧹 Nettoyage manuel forcé du chat global...');
        
        // Nettoyer les anciens messages
        this.cleanOldMessages();
        
        // Nettoyer le cache des emotes expiré
        this.cleanExpiredCache();
        
        // Forcer le garbage collection si disponible (Chrome DevTools)
        if (window.gc && typeof window.gc === 'function') {
            try {
                window.gc();
                console.log('🗑️ Garbage collection forcé');
            } catch (e) {
                // Ignore les erreurs si gc() n'est pas disponible
            }
        }
        
        console.log('✅ Nettoyage manuel terminé');
    }

    /**
     * Formate l'heure d'un message
     */
    formatTime(timestamp) {
        return timestamp.toLocaleTimeString(document.documentElement.lang || navigator.language, {
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    /**
     * Simule des messages de chat pour la démonstration
     * Dans une vraie implémentation, ceci serait remplacé par une vraie connexion IRC/WebSocket
     */
    simulateMessages() {
        const sampleMessages = [
            "Salut tout le monde ! 👋",
            "Excellent stream aujourd'hui !",
            "GG pour cette partie 🎮 LuL",
            "Quelqu'un a le lien du jeu ?",
            "POGGERS KEKW",
            "Merci pour le stream ! EZ",
            "First ! 🥇 Pepega",
            "Incroyable ce gameplay MonkaS",
            "Hype hype hype ! 5Head",
            "Bonne chance pour la suite OMEGALUL"
        ];

        const sampleUsernames = [
            "GamerPro123", "StreamFan", "ChatMaster", "ViewerOne", "TwitchLover",
            "GameEnthusiast", "StreamWatcher", "ChatUser", "Follower123", "Subscriber"
        ];

        // Simuler des messages périodiques
        const streamers = Array.from(this.activeStreams.keys());
        if (streamers.length === 0) return;

        const sendRandomMessage = () => {
            if (!this.isGlobalMode) return;

            const randomStreamer = streamers[Math.floor(Math.random() * streamers.length)];
            const randomUsername = sampleUsernames[Math.floor(Math.random() * sampleUsernames.length)];
            const randomMessage = sampleMessages[Math.floor(Math.random() * sampleMessages.length)];

            this.addMessage(randomStreamer, randomUsername, randomMessage);

            // Programmer le prochain message
            setTimeout(sendRandomMessage, Math.random() * 5000 + 2000); // Entre 2 et 7 secondes
        };

        // Démarrer la simulation
        setTimeout(sendRandomMessage, 1000);
    }

    /**
     * Met à jour la liste des streamers actifs
     */
    async updateActiveStreamers(streamers) {
        if (!this.isGlobalMode) return;

        // Identifier les nouveaux streamers
        const currentStreamers = new Set(Array.from(this.activeStreams.keys()));
        const newStreamers = streamers.filter(streamer => !currentStreamers.has(streamer.id));
        const removedStreamers = Array.from(currentStreamers).filter(id => 
            !streamers.find(streamer => streamer.id === id)
        );

        // Déconnecter des anciens canaux
        if (this.twitchClient && this.twitchClient.isClientConnected()) {
            for (const streamerId of removedStreamers) {
                const streamer = this.activeStreams.get(streamerId);
                if (streamer) {
                    try {
                        await this.twitchClient.leaveChannel(streamer.login);
                        // Déconnecté du chat du streamer
                    } catch (error) {
                        console.error(`❌ Erreur déconnexion chat ${streamer.display_name}:`, error);
                    }
                }
            }
        }

        // Mettre à jour la map des streamers
        this.activeStreams.clear();
        streamers.forEach(streamer => {
            this.activeStreams.set(streamer.id, {
                id: streamer.id,
                login: streamer.login,
                display_name: streamer.display_name,
                profile_image_url: streamer.profile_image_url,
                color: this.generateStreamerColor(streamer.id)
            });
        });

        // Connecter aux nouveaux canaux Twitch
        if (newStreamers.length > 0 && this.twitchClient && this.twitchClient.isClientConnected()) {
            // Connexion aux nouveaux canaux
            let connectedChannels = 0;
            
            for (const streamer of newStreamers) {
                try {
                    await this.twitchClient.joinChannel(streamer.login);
                    connectedChannels++;
                    // Connecté au nouveau chat du streamer
                } catch (error) {
                    console.error(`❌ Erreur connexion nouveau chat ${streamer.display_name}:`, error);
                }
            }
            
            // Nouveaux canaux connectés
        }

        // Re-rendre l'interface si nécessaire
        const streamersList = this.chatContainer.querySelector('.streamers-list');
        if (streamersList) {
            streamersList.innerHTML = Array.from(this.activeStreams.values()).map(streamer => `
                <div class="streamer-indicator" data-streamer-id="${streamer.id}" style="border-color: ${streamer.color}">
                    <img src="${streamer.profile_image_url}" alt="${streamer.display_name}" class="streamer-avatar">
                    <div class="separator"></div>
                    <span class="streamer-name">${streamer.display_name}</span>
                </div>
            `).join('');
            
            // Ajouter les événements de clic pour chaque streamer
            streamersList.querySelectorAll('.streamer-indicator').forEach(indicator => {
                indicator.addEventListener('click', (e) => {
                    const streamerId = e.currentTarget.dataset.streamerId;
                    this.toggleStreamerVisibility(streamerId);
                });
            });
        }
    }

    /**
     * Bascule la visibilité d'un streamer dans le chat global
     */
    toggleStreamerVisibility(streamerId) {
        const indicators = this.chatContainer.querySelectorAll(`[data-streamer-id="${streamerId}"]`);
        if (indicators.length === 0) return;

        if (this.hiddenStreamers.has(streamerId)) {
            // Afficher le streamer
            this.hiddenStreamers.delete(streamerId);
            indicators.forEach(indicator => indicator.classList.remove('hidden'));
        } else {
            // Cacher le streamer
            this.hiddenStreamers.add(streamerId);
            indicators.forEach(indicator => indicator.classList.add('hidden'));
        }

        // Filtrer les messages affichés
        this.filterMessages();
    }

    /**
     * Filtre les messages selon les streamers cachés
     */
    filterMessages() {
        const messagesContainer = document.getElementById('global-chat-messages');
        if (!messagesContainer) return;

        const messages = messagesContainer.querySelectorAll('.chat-message');
        messages.forEach((messageElement) => {
            const streamerId = messageElement.dataset.streamerId;
            if (streamerId && this.hiddenStreamers.has(streamerId)) {
                messageElement.style.display = 'none';
            } else {
                messageElement.style.display = 'flex';
            }
        });
    }

    /**
     * Configure le listener de scroll pour détecter quand l'utilisateur scroll
     */
    setupScrollListener() {
        const messagesContainer = this.chatContainer.querySelector('#global-chat-messages');
        if (!messagesContainer) return;

        this.shouldAutoScroll = true;
        
        messagesContainer.addEventListener('scroll', () => {
            // Vérifier si l'utilisateur est en bas
            const isAtBottom = messagesContainer.scrollTop + messagesContainer.clientHeight >= messagesContainer.scrollHeight - 5;
            this.shouldAutoScroll = isAtBottom;
        });
    }

    /**
     * Gère le scroll automatique intelligent
     */
    handleAutoScroll(messagesContainer) {
        if (!messagesContainer) return;
        
        // Si l'auto-scroll est activé, scroller vers le bas
        if (this.shouldAutoScroll) {
            messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }
    }

    /**
     * Configure le redimensionnement de la section des streamers
     */
    setupChatResizer() {
        const resizer = document.getElementById('chat-resizer');
        const streamersSection = document.getElementById('global-chat-streamers');
        const messagesSection = document.getElementById('global-chat-messages');
        
        if (!resizer || !streamersSection || !messagesSection) return;
        
        let isResizing = false;
        let startY = 0;
        let startStreamersHeight = 0;
        
        // Récupérer la hauteur initiale sauvegardée ou utiliser la valeur par défaut
        const savedHeight = localStorage.getItem('global-chat-streamers-height');
        if (savedHeight) {
            streamersSection.style.height = savedHeight + 'px';
        }
        
        const startResize = (e) => {
            isResizing = true;
            startY = e.clientY || e.touches[0].clientY;
            startStreamersHeight = parseInt(window.getComputedStyle(streamersSection).height, 10);
            
            document.body.style.cursor = 'ns-resize';
            document.body.style.userSelect = 'none';
            resizer.classList.add('resizing');
            
            e.preventDefault();
        };
        
        const doResize = (e) => {
            if (!isResizing) return;
            
            const currentY = e.clientY || e.touches[0].clientY;
            const deltaY = currentY - startY;
            
            // Calculer les nouvelles hauteurs (minimum 60px, maximum 300px)
            const newStreamersHeight = Math.max(60, Math.min(300, startStreamersHeight - deltaY));
            
            // Appliquer la nouvelle hauteur
            streamersSection.style.height = newStreamersHeight + 'px';
            
            e.preventDefault();
        };
        
        const stopResize = () => {
            if (!isResizing) return;
            
            isResizing = false;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            resizer.classList.remove('resizing');
            
            // Sauvegarder la hauteur dans localStorage
            const currentHeight = parseInt(window.getComputedStyle(streamersSection).height, 10);
            localStorage.setItem('global-chat-streamers-height', currentHeight);
        };
        
        // Event listeners pour la souris
        resizer.addEventListener('mousedown', startResize);
        document.addEventListener('mousemove', doResize);
        document.addEventListener('mouseup', stopResize);
        
        // Event listeners pour le tactile
        resizer.addEventListener('touchstart', startResize, { passive: false });
        document.addEventListener('touchmove', doResize, { passive: false });
        document.addEventListener('touchend', stopResize);
        
        // Empêcher la sélection de texte pendant le redimensionnement
        resizer.addEventListener('selectstart', (e) => e.preventDefault());
    }
}
